import React, {useMemo, useRef, useState} from 'react';
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {Button, Select, Typography, Tooltip as AntTooltip, Alert} from 'antd';
import {ArrowLeftOutlined, FullscreenOutlined, ZoomInOutlined, ZoomOutOutlined, LockOutlined, CrownOutlined, WarningOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {useScopedNavigate} from '../MarketerView/useScopedNavigate';
import {useIntl} from 'react-intl';
import {IBrandAnalyticsMetricOption, IBrandAnalyticsMonthlyData} from '../../services/nestjs-base-api';

const { Title } = Typography;
const { Option } = Select;

interface BrandAnalyticsChartProps {
  data: IBrandAnalyticsMonthlyData[];
  availableMetrics: IBrandAnalyticsMetricOption[];
  brandName: string;
  brandHash: string;
  onLineClick?: (metric: string, level: string) => void;
  drillDownLevel?: 'latent' | 'measuredElement' | 'statements';
  parentMetric?: string | null;
  onBackClick?: () => void;
  isLoading?: boolean;
  onDrillDown?: (parentMetric: string, constituents: string[], currentSelectedMetrics: string[]) => void;
  previousSelectedMetrics?: string[];
  // For custom charts: external control of selected metrics
  initialSelectedMetrics?: string[];
  onMetricsChange?: (metrics: string[]) => void;
  customTitle?: string; // For custom charts: override the default title
  headerActions?: React.ReactNode; // Additional actions to render in the header
  // For subscription tier restrictions
  isElementLocked?: (elementName: string) => boolean;
  hasUnrestrictedAccess?: boolean;
  // For low response count warnings
  minResponseThreshold?: number; // Minimum responses required (default 10)
  lowConfidenceThreshold?: number; // Below this shows "low confidence" warning (default 50)
}

const BrandAnalyticsChart: React.FC<BrandAnalyticsChartProps> = ({
  data,
  availableMetrics,
  brandName,
  brandHash,
  onLineClick,
  drillDownLevel = 'latent',
  parentMetric = null,
  onBackClick,
  isLoading = false,
  onDrillDown,
  previousSelectedMetrics,
  initialSelectedMetrics,
  onMetricsChange,
  customTitle,
  headerActions,
  isElementLocked,
  hasUnrestrictedAccess = true,
  minResponseThreshold = 10,
  lowConfidenceThreshold = 50
}) => {
  const navigate = useScopedNavigate();
  const intl = useIntl();
  // Determine if we're in controlled mode (custom chart with external state)
  const isControlled = initialSelectedMetrics !== undefined && onMetricsChange !== undefined;

  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(initialSelectedMetrics || []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectValue, setSelectValue] = useState<string | null>(null);
  const [selectKey, setSelectKey] = useState(0);

  // Y-axis scale mode: 'full' (-100 to +100) or 'auto' (based on data range)
  const [scaleMode, setScaleMode] = useState<'full' | 'auto'>(() => {
    try {
      const stored = localStorage.getItem(`brand-analytics-scale-${brandHash}`);
      return (stored === 'auto' || stored === 'full') ? stored : 'full';
    } catch (e) {
      return 'full';
    }
  });
  
  // State for disambiguation popup
  const [disambiguationPopup, setDisambiguationPopup] = useState<{
    show: boolean;
    x: number;
    y: number;
    measures: Array<{
      metric: string;
      value: number;
      color: string;
    }>;
    clickedData: any;
  }>({
    show: false,
    x: 0,
    y: 0,
    measures: [],
    clickedData: null
  });

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const lastClickPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // State to track current active data point from tooltip
  const [activeDataPoint, setActiveDataPoint] = useState<any>(null);
  
  // Track if this is initial load to avoid auto-adding metrics when user removes all
  const isInitialLoad = React.useRef(true);

  // localStorage helpers for selected metrics
  const getStoredMetrics = React.useCallback(() => {
    try {
      const stored = localStorage.getItem(`brand-analytics-metrics-${brandHash}`);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, [brandHash]);

  const saveMetricsToStorage = React.useCallback((metrics: string[]) => {
    try {
      localStorage.setItem(`brand-analytics-metrics-${brandHash}`, JSON.stringify(metrics));
    } catch (e) {
      // Ignore storage errors
    }
  }, [brandHash]);

  // Sync with external state when in controlled mode
  React.useEffect(() => {
    if (isControlled && initialSelectedMetrics) {
      setSelectedMetrics(initialSelectedMetrics);
    }
  }, [isControlled, initialSelectedMetrics]);

  // Initialize with a good default selection when availableMetrics changes (only in uncontrolled mode)
  React.useEffect(() => {
    // Skip auto-selection if in controlled mode
    if (isControlled) return;

    // Helper to check if a metric is locked
    const isLocked = (metricKey: string) => isElementLocked ? isElementLocked(metricKey) : false;

    if (availableMetrics.length > 0) {
      // If we're in drill-down mode, auto-select ALL constituent measures (already filtered by parent)
      if (parentMetric) {
        // Select all available metrics when drilling down
        setSelectedMetrics(availableMetrics.map(m => m.key));
      } else if (previousSelectedMetrics && previousSelectedMetrics.length > 0) {
        // Restore previous selection when going back
        // Filter to only include metrics that are available and not locked
        const validPreviousMetrics = previousSelectedMetrics.filter(metric =>
          availableMetrics.some(am => am.key === metric) && !isLocked(metric)
        );
        setSelectedMetrics(validPreviousMetrics);
        isInitialLoad.current = false; // Mark as no longer initial load
      } else if (selectedMetrics.length === 0 && !parentMetric && isInitialLoad.current) {
        // Check for stored metrics first
        const storedMetrics = getStoredMetrics();
        if (storedMetrics && storedMetrics.length > 0) {
          // Filter stored metrics to only include ones that are still available and not locked
          const validStoredMetrics = storedMetrics.filter((metric: string) =>
            availableMetrics.some(am => am.key === metric) && !isLocked(metric)
          );
          if (validStoredMetrics.length > 0) {
            setSelectedMetrics(validStoredMetrics);
            isInitialLoad.current = false;
            return;
          }
        }

        // Only set defaults on initial load for top level, not when user manually removes all metrics
        // Filter out locked elements from default selection
        const defaultSelection = availableMetrics
          .filter(m => (m.key === 'Brand Equity' || m.type === 'latentElement') && !isLocked(m.key))
          .slice(0, 4)  // Start with 4 metrics so users can add more
          .map(m => m.key);
        setSelectedMetrics(defaultSelection);
        isInitialLoad.current = false; // Mark as no longer initial load
      }
    }
  }, [isControlled, availableMetrics, parentMetric, previousSelectedMetrics, selectedMetrics.length, getStoredMetrics, isElementLocked]);

  // Save selected metrics to localStorage whenever they change (only for uncontrolled mode and top level)
  React.useEffect(() => {
    if (!isControlled && !parentMetric && selectedMetrics.length > 0) {
      saveMetricsToStorage(selectedMetrics);
    }
  }, [isControlled, selectedMetrics, parentMetric, saveMetricsToStorage]);

  // Filter out locked metrics from current selection when tier access becomes available
  const hasFilteredLockedMetrics = React.useRef(false);
  React.useEffect(() => {
    if (isControlled || parentMetric) return;
    if (!isElementLocked || hasFilteredLockedMetrics.current) return;

    const lockedMetrics = selectedMetrics.filter(m => isElementLocked(m));
    if (lockedMetrics.length > 0) {
      const unlockedMetrics = selectedMetrics.filter(m => !isElementLocked(m));
      setSelectedMetrics(unlockedMetrics);
      hasFilteredLockedMetrics.current = true;
    } else {
      // Mark as checked even if no locked metrics found
      hasFilteredLockedMetrics.current = true;
    }
  }, [isControlled, parentMetric, isElementLocked, selectedMetrics]);

  // Save scale mode preference to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem(`brand-analytics-scale-${brandHash}`, scaleMode);
    } catch (e) {
      // Ignore storage errors
    }
  }, [scaleMode, brandHash]);

  const handleScaleModeToggle = () => {
    setScaleMode(prev => prev === 'full' ? 'auto' : 'full');
  };

  // Chart color palette
  const colors = [
    'var(--chart-color-1)',
    'var(--chart-color-2)',
    'var(--chart-color-3)',
    'var(--chart-color-4)',
    'var(--chart-color-5)',
    'var(--chart-color-6)',
    'var(--chart-color-7)',
    'var(--chart-color-8)',
  ];

  // Function to get consistent color for a metric name across all charts
  const getMetricColor = (metricName: string): string => {
    // Simple hash function to get consistent index for metric name
    let hash = 0;
    for (let i = 0; i < metricName.length; i++) {
      hash = metricName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Transform data based on drill-down level
  const chartData = useMemo(() => {
    if (!data || !data.length) return [];

    // SYR-59 — bucket label is now the bucket *end* date (YYYY-MM-DD).
    // Legacy month strings (YYYY-MM) still parse correctly via the fallback.
    const parseBucketDate = (m: string): Date => {
      return /^\d{4}-\d{2}-\d{2}$/.test(m)
        ? new Date(m)
        : new Date(m + '-01');
    };
    const formatBucketLabel = (m: string): string => {
      const d = parseBucketDate(m);
      // Long-period months use "Jan 25" style; sub-monthly buckets use "May 12" style.
      const isFullDate = /^\d{4}-\d{2}-\d{2}$/.test(m);
      return isFullDate
        ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    };

    // First pass: transform the data with metric values
    const transformedData = data.map(monthData => {
      // Check if this month has insufficient responses
      const hasSufficientResponses = monthData.totalResponses >= minResponseThreshold;
      const isLowConfidence = hasSufficientResponses && monthData.totalResponses < lowConfidenceThreshold;

      const transformed: any = {
        month: formatBucketLabel(monthData.month),
        fullDate: monthData.month,
        totalResponses: monthData.totalResponses,
        isLowConfidence
      };

      // Add all possible latent element scores (null if not present OR insufficient responses)
      const allElements = [
        'Brand Equity', 'Brand Loyalty', 'Perceived Quality', 'Associations', 'Market Awareness',
        'Attitudinal Loyalty', 'Behavioural Loyalty', 'Brand Attachment',
        'Alignment Depth', 'Willingness to Pay a Premium', 'Quality Value Tradeoff (PQV)'
      ];

      allElements.forEach(element => {
        // If insufficient responses, set to null so no data point is shown
        if (!hasSufficientResponses) {
          transformed[element] = null;
          return;
        }
        const elementData = monthData.latentElementScores?.find(le => le.element === element);
        if (elementData) {
          transformed[element] = elementData.score;
        } else {
          // Missing data - set to null so line doesn't show
          transformed[element] = null;
        }
      });

      // If drilling down into input elements, add those
      if (drillDownLevel === 'measuredElement' && parentMetric) {
        // Find the latent element and add ALL its input elements (both QC and LE)
        const latentElement = monthData.latentElementScores?.find(le => le.element === parentMetric);
        if (latentElement?.inputs) {
          latentElement.inputs.forEach(input => {
            // If insufficient responses, set to null
            if (!hasSufficientResponses) {
              transformed[input.name] = null;
              return;
            }
            if (input.type === 'QC') {
              // QC = Question Category (measured element)
              transformed[input.name] = input.value;
            } else if (input.type === 'LE') {
              // LE = Latent Element - get the actual score from latentElementScores
              const leData = monthData.latentElementScores?.find(le => le.element === input.name);
              transformed[input.name] = leData?.score || input.value;
            }
          });
        }
      }

      return transformed;
    });

    return transformedData;
  }, [data, drillDownLevel, parentMetric, minResponseThreshold, lowConfidenceThreshold]);

  // Compute gap segments for each metric and add gap-specific data keys to chartData
  // Each gap gets its own dataKey like "Brand Equity_gap0", "Brand Equity_gap1", etc.
  const { chartDataWithGaps, gapKeys } = useMemo(() => {
    if (!chartData || chartData.length < 2) {
      return { chartDataWithGaps: chartData, gapKeys: {} };
    }

    // Get all metric keys from the first data point
    const metricKeys = Object.keys(chartData[0]).filter(
      key => key !== 'month' && key !== 'fullDate' && key !== 'totalResponses' && key !== 'isLowConfidence'
    );

    // Find gaps for each metric and track which indices are gap endpoints
    const gapKeysByMetric: Record<string, string[]> = {};
    const gapEndpoints: Record<number, Record<string, number>> = {}; // idx -> gapKey -> value

    metricKeys.forEach(metric => {
      gapKeysByMetric[metric] = [];
      let gapIndex = 0;

      // Find all gaps for this metric
      let i = 0;
      while (i < chartData.length) {
        // Find next data point (non-null value)
        while (i < chartData.length && (chartData[i][metric] === null || chartData[i][metric] === undefined)) {
          i++;
        }
        if (i >= chartData.length) break;

        i++;

        // Skip consecutive data points (no gap)
        while (i < chartData.length && chartData[i][metric] !== null && chartData[i][metric] !== undefined) {
          i++;
        }

        // Now i is at a null (gap start) or end of array
        if (i >= chartData.length) break;

        const gapStartIdx = i;

        // Find the end of the gap (next non-null)
        while (i < chartData.length && (chartData[i][metric] === null || chartData[i][metric] === undefined)) {
          i++;
        }

        // If we found data after the gap, record this gap
        if (i < chartData.length) {
          const beforeGapIdx = gapStartIdx - 1;
          const afterGapIdx = i;
          const gapKey = `${metric}_gap${gapIndex}`;
          gapKeysByMetric[metric].push(gapKey);
          gapIndex++;

          // Record the gap endpoints (without mutating chartData)
          if (!gapEndpoints[beforeGapIdx]) gapEndpoints[beforeGapIdx] = {};
          if (!gapEndpoints[afterGapIdx]) gapEndpoints[afterGapIdx] = {};
          gapEndpoints[beforeGapIdx][gapKey] = chartData[beforeGapIdx][metric];
          gapEndpoints[afterGapIdx][gapKey] = chartData[afterGapIdx][metric];
        }
      }
    });

    // Collect all gap keys across all metrics
    const allGapKeys = Object.values(gapKeysByMetric).flat();

    // Now create the enhanced chart data with gap keys
    const enhanced = chartData.map((point, idx) => {
      const newPoint = { ...point };

      // Add all gap keys, setting them to null unless this index is a gap endpoint
      allGapKeys.forEach(gapKey => {
        newPoint[gapKey] = gapEndpoints[idx]?.[gapKey] ?? null;
      });

      return newPoint;
    });

    return { chartDataWithGaps: enhanced, gapKeys: gapKeysByMetric };
  }, [chartData]);

  // Calculate Y-axis domain based on data and scale mode
  const yAxisDomain = useMemo(() => {
    if (scaleMode === 'full') {
      return [-100, 100] as [number, number];
    }

    // Auto scale: find min and max values in the data for selected metrics
    let min = Infinity;
    let max = -Infinity;

    chartData.forEach(dataPoint => {
      selectedMetrics.forEach(metric => {
        const value = dataPoint[metric];
        if (value !== null && value !== undefined && !isNaN(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      });
    });

    // If no valid data, fall back to full scale
    if (min === Infinity || max === -Infinity) {
      return [-100, 100] as [number, number];
    }

    // Add 10% padding to the range for better visualization
    const range = max - min;
    const padding = Math.max(range * 0.1, 5); // At least 5 points of padding

    return [
      Math.max(-100, Math.floor(min - padding)),
      Math.min(100, Math.ceil(max + padding))
    ] as [number, number];
  }, [scaleMode, chartData, selectedMetrics]);

  // Check if any months in the chart have low confidence data (enough responses to show but below threshold)
  const hasAnyLowConfidenceData = useMemo(() => {
    return chartData.some(dataPoint => dataPoint.isLowConfidence);
  }, [chartData]);

  // Check if any buckets are hidden because they fell below the minimum response
  // threshold — these render as dashed gaps with no dot. We only surface the
  // explanation when such gaps actually exist, so clean charts aren't nagged.
  const hasAnySuppressedData = useMemo(() => {
    return (data ?? []).some(
      monthData =>
        monthData.totalResponses > 0 &&
        monthData.totalResponses < minResponseThreshold,
    );
  }, [data, minResponseThreshold]);

  const handleLineClick = (event: any) => {
    if (onLineClick && event && event.dataKey) {
      onLineClick(event.dataKey, drillDownLevel);
    }
  };

  // Function to find all measures at a specific data point
  const findOverlappingMeasures = (clickedMetric: string, monthLabel?: string): Array<{
    metric: string;
    value: number;
    color: string;
  }> => {
    
    if (!chartData) {
        return [];
    }
    
    // If no monthLabel provided, try to use activeDataPoint
    const targetMonth = monthLabel || activeDataPoint?.label;
    
    if (!targetMonth) {
      console.log('No target month available');
      return [];
    }
    
    const clickedDataPoint = chartData.find(d => d.month === targetMonth);
    
    if (!clickedDataPoint) {
      return [];
    }
    
    const tolerance = 6.0; // Even wider tolerance to catch visually overlapping measures
    const overlappingMeasures: Array<{ metric: string; value: number; color: string }> = [];
    const clickedValue = clickedDataPoint[clickedMetric];
    
    
    if (clickedValue === null || clickedValue === undefined) {
      return [];
    }
    
    
    // Find ALL measures that are within tolerance of the clicked measure's value
    selectedMetrics.forEach((metric) => {
      const value = clickedDataPoint[metric];
      if (value !== null && value !== undefined) {
        const difference = Math.abs(value - clickedValue);

        // Include if within tolerance of the clicked measure's value
        if (difference <= tolerance) {
          overlappingMeasures.push({
            metric,
            value,
            color: getMetricColor(metric)
          });
        }
      }
    });
    
    return overlappingMeasures;
  };

  // Function to handle drill-down for a specific measure
  const handleMeasureDrillDown = (metric: string, dotData?: any) => {
    if (!onDrillDown || !data) return;
    
    const drillDownOptions = getDrillDownData(metric, data, 'measuredElement');
    
    if (drillDownOptions && drillDownOptions.length > 0) {
        onDrillDown(metric, drillDownOptions, selectedMetrics);
    } else {
      console.log('No drill down options found for', metric);
    }
    
    // Close the disambiguation popup
    setDisambiguationPopup(prev => ({ ...prev, show: false }));
  };

  // Handle escape key to close popup
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && disambiguationPopup.show) {
        setDisambiguationPopup(prev => ({ ...prev, show: false }));
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [disambiguationPopup.show]);

  // Custom tooltip component - display only (drill-down via line clicks)
  const customTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    // Filter out null values, gap entries, and deduplicate (we have multiple lines per metric)
    const seen = new Set<string>();
    const validPayload = payload
      .filter((entry: any) => {
        // Skip gap entries - they're only for rendering dashed lines across gaps
        // Gap keys look like "Brand Equity_gap0", "Brand Equity_gap1", etc.
        if (entry.dataKey?.includes('_gap')) {
          return false;
        }
        if (entry.value === null || seen.has(entry.dataKey)) {
          return false;
        }
        seen.add(entry.dataKey);
        return true;
      })
      // Sort by value descending to match visual order of dots on chart
      .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0));

    if (!validPayload.length) {
      return null;
    }

    // Get the data point for this tooltip to access response count
    const dataPoint = validPayload[0]?.payload;
    const totalResponses = dataPoint?.totalResponses;
    const isLowConfidence = dataPoint?.isLowConfidence;

    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        border: '1px solid #e1e5e9',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(8px)',
        minWidth: '280px',
        maxWidth: '340px'
      }}>
        {/* Header with date and response count */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: isLowConfidence ? '#f59e0b' : '#2563eb',
              marginRight: '8px'
            }} />
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1f2937'
            }}>
              {label}
            </span>
          </div>
          {totalResponses !== undefined && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: isLowConfidence ? '#f59e0b' : '#6b7280'
            }}>
              {isLowConfidence && <WarningOutlined style={{ fontSize: '11px' }} />}
              <span>{totalResponses} responses</span>
            </div>
          )}
        </div>

        {/* Low confidence warning */}
        {isLowConfidence && (
          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '6px',
            padding: '8px 10px',
            marginBottom: '12px',
            fontSize: '11px',
            color: '#b45309',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <WarningOutlined style={{ fontSize: '12px' }} />
            <span>Low sample size - results may be less reliable</span>
          </div>
        )}

        {/* Metrics */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {validPayload.map((entry: any) => {
            const isMainMetric = entry.dataKey === 'overallBrandEquityScore';
            const isDrillable = !parentMetric; // All metrics are drillable when not already drilled down
            const score = entry.value?.toFixed(1) || '0.0';
            const color = entry.color || getMetricColor(entry.dataKey);

            return (
              <div
                key={entry.dataKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: isMainMetric ? '8px' : '6px',
                  backgroundColor: isMainMetric ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
                  borderRadius: isMainMetric ? '8px' : '4px',
                  border: isMainMetric ? '1px solid rgba(37, 99, 235, 0.1)' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    marginRight: '10px',
                    boxShadow: `0 0 0 2px ${color}20`
                  }} />

                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: isMainMetric ? '13px' : '12px',
                      fontWeight: isMainMetric ? 600 : 500,
                      color: '#374151',
                      marginBottom: '2px'
                    }}>
                      {entry.name === 'overallBrandEquityScore' ? 'Brand Equity' : entry.name}
                      {isDrillable && (
                        <span style={{ fontSize: '10px', color: '#6b7280', marginLeft: '4px' }}>
                          (click dot to drill down)
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: isMainMetric ? '16px' : '14px',
                    fontWeight: isMainMetric ? 700 : 600,
                    color: entry.value >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {entry.value >= 0 ? '+' : ''}{score}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Instruction text */}
        {!parentMetric && (
          <div style={{
            marginTop: '12px',
            paddingTop: '8px',
            borderTop: '1px solid #f0f0f0',
            fontSize: '11px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Click on chart dots to drill down into constituent measures
          </div>
        )}
      </div>
    );
  };


  const getTitle = () => {
    if (parentMetric) {
      return `"${parentMetric}" Component Elements`;
    }
    return customTitle || `Brand Analytics Trends`;
  };


  const renderBreadcrumb = () => {
    if (!parentMetric) return null;

    return (
      <div className="brand-analytics-breadcrumb">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={onBackClick}
          type="text"
          size="small"
        >
          Back
        </Button>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item">Brand Analytics</span>
        {parentMetric && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item active">{parentMetric}</span>
          </>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="brand-analytics-loading">
        <div>Loading chart data...</div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="brand-analytics-empty">
        <Title level={4}>No data available</Title>
        <p>No analytics data found for the selected time period.</p>
      </div>
    );
  }

  return (
    <div
      className={`brand-analytics-chart-container ${isFullscreen ? 'fullscreen' : ''}`}
      ref={chartContainerRef}
      style={{ position: 'relative' }}
      onClick={(e) => {
        if (chartContainerRef.current) {
          const rect = chartContainerRef.current.getBoundingClientRect();
          lastClickPosition.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          };
        }
      }}
    >
      {renderBreadcrumb()}
      
      <div className="chart-header">
        <div>
          <Title level={3} className="chart-title">{getTitle()}</Title>
        </div>

        <div className="chart-actions">
          {headerActions}
          <AntTooltip title={scaleMode === 'full' ? 'Zoom to data range' : 'Show full scale (-100 to +100)'}>
            <Button
              icon={scaleMode === 'full' ? <ZoomInOutlined /> : <ZoomOutOutlined />}
              onClick={handleScaleModeToggle}
              type="text"
            >
              {scaleMode === 'full' ? 'Auto Scale' : 'Full Scale'}
            </Button>
          </AntTooltip>
          <Button
            icon={<FullscreenOutlined />}
            onClick={() => setIsFullscreen(!isFullscreen)}
            type="text"
          />
        </div>
      </div>

      {/* Custom Legend - Above Chart */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        {selectedMetrics.map((metric) => {
          const color = getMetricColor(metric);
          const metricLabel = availableMetrics.find(m => m.key === metric)?.label || metric;
          return (
            <div
              key={metric}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: 'white',
                borderRadius: '20px',
                border: '1px solid #e0e0e0',
                fontSize: '13px',
                fontWeight: 500,
                cursor: parentMetric ? 'default' : 'pointer' // No removal in drill-down mode
              }}
              onClick={() => {
                // Only allow removal if not in drill-down mode
                if (!parentMetric) {
                  const newSelected = selectedMetrics.filter(m => m !== metric);
                  setSelectedMetrics(newSelected);
                  // Notify parent if in controlled mode
                  if (isControlled && onMetricsChange) {
                    onMetricsChange(newSelected);
                  }
                }
              }}
            >
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: color
                }}
              />
              <span style={{ color: '#374151' }}>{metricLabel}</span>
              {!parentMetric && ( // Only show remove button when not in drill-down
                <span style={{ color: '#9ca3af', fontSize: '11px' }}>✕</span>
              )}
            </div>
          );
        })}
        
        {/* Only show dropdown when NOT in drill-down mode and less than 6 metrics selected */}
        {!parentMetric && selectedMetrics.length < 6 && availableMetrics.filter(m => !selectedMetrics.includes(m.key)).length > 0 && (
          <AntTooltip title={!hasUnrestrictedAccess ? intl.formatMessage({ id: 'subscription.upgrade.elementsRequireUpgrade' }) : undefined}>
            <Select
              key={selectKey}
              placeholder="Add metric..."
              style={{
                minWidth: '280px',
                height: '32px',
                fontSize: '13px'
              }}
              size="small"
              value={selectValue}
              dropdownStyle={{ minWidth: '320px' }}
              allowClear={false}
              showSearch={false}
              suffixIcon={!hasUnrestrictedAccess ? <LockOutlined style={{ color: '#9ca3af', fontSize: 11 }} /> : undefined}
              onChange={(value) => {
                // Check if the element is locked
                const locked = isElementLocked ? isElementLocked(value) : false;
                if (locked) {
                  return; // Don't allow selection of locked elements
                }
                if (selectedMetrics.length < 6 && value) {
                  const newSelected = [...selectedMetrics, value];
                  setSelectedMetrics(newSelected);
                  setSelectValue(null); // Reset to placeholder immediately
                  setSelectKey(prev => prev + 1); // Force re-render
                  // Notify parent if in controlled mode
                  if (isControlled && onMetricsChange) {
                    onMetricsChange(newSelected);
                  }
                }
              }}
            >
              {availableMetrics
                .filter(m => !selectedMetrics.includes(m.key))
                  .sort((a, b) => {
                    // Always put Brand Equity first
                    if (a.key === 'Brand Equity') return -1;
                    if (b.key === 'Brand Equity') return 1;
                    // Sort locked elements to the end
                    const aLocked = isElementLocked ? isElementLocked(a.key) : false;
                    const bLocked = isElementLocked ? isElementLocked(b.key) : false;
                    if (aLocked && !bLocked) return 1;
                    if (!aLocked && bLocked) return -1;
                    // Then sort alphabetically by label
                    return a.label.localeCompare(b.label);
                  })
                  .map(metric => {
                    const locked = isElementLocked ? isElementLocked(metric.key) : false;
                    return (
                      <Option key={metric.key} value={metric.key} disabled={locked}>
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          color: locked ? '#9ca3af' : undefined
                        }}>
                          <span>{metric.label}</span>
                          {locked && <LockOutlined style={{ color: '#9ca3af', fontSize: 11, marginLeft: 8 }} />}
                        </span>
                      </Option>
                    );
                  })
              }
            </Select>
          </AntTooltip>
        )}
      </div>

      {/* Upgrade prompt for locked elements */}
      {!hasUnrestrictedAccess && !parentMetric && (
        <Alert
          type="info"
          showIcon
          icon={<LockOutlined />}
          message={
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{intl.formatMessage({ id: 'subscription.upgrade.elementsRequireUpgradeToAccess' })}</span>
              <Button
                type="link"
                size="small"
                icon={<CrownOutlined />}
                onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                style={{ padding: 0, height: 'auto' }}
              >
                {intl.formatMessage({ id: 'subscription.upgrade.unlockAllElements' })}
              </Button>
            </span>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Low confidence warning banner */}
      {hasAnyLowConfidenceData && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={intl.formatMessage(
            { id: 'brandAnalytics.lowConfidence.warning' },
            { threshold: lowConfidenceThreshold }
          )}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Missing-data notice — only when buckets were actually suppressed */}
      {hasAnySuppressedData && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message={intl.formatMessage(
            { id: 'brandAnalytics.missingData.info' },
            { threshold: minResponseThreshold }
          )}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      <ResponsiveContainer width="100%" height={isFullscreen ? 600 : 400}>
        <LineChart 
          data={chartDataWithGaps}
          onClick={handleLineClick}
          onMouseMove={(data: any) => {
            if (data && data.activePayload && data.activePayload.length && data.activeLabel) {
              const newDataPoint = { 
                payload: data.activePayload[0].payload, 
                label: data.activeLabel 
              };
              // Only update if the label has changed to avoid constant re-renders
              if (!activeDataPoint || activeDataPoint.label !== newDataPoint.label) {
                setActiveDataPoint(newDataPoint);
              }
            }
          }}
          onMouseLeave={() => setActiveDataPoint(null)}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="var(--border-color)" 
            opacity={0.5}
          />
          <XAxis 
            dataKey="month" 
            stroke="var(--text-secondary)"
            fontSize={12}
            tick={{ fill: 'var(--text-secondary)' }}
          />
          <YAxis
            stroke="var(--text-secondary)"
            fontSize={12}
            tick={{ fill: 'var(--text-secondary)' }}
            domain={yAxisDomain}
          />
          <Tooltip 
            content={customTooltip}
            cursor={{ stroke: 'var(--primary-color)', strokeWidth: 1 }}
          />
          <Legend content={() => null} />

          {selectedMetrics.map((metric) => {
            const isDrillable = !parentMetric; // All metrics are drillable when not already drilled down
            const metricColor = getMetricColor(metric);

            // Custom dot renderer that shows amber ring for low confidence data points
            const renderCustomDot = (props: any): React.ReactElement => {
              const { cx, cy, payload, stroke, fill } = props;
              if (cx === undefined || cy === undefined || payload[metric] === null || payload[metric] === undefined) {
                return <></>;
              }
              const isLowConf = payload.isLowConfidence;

              return (
                <g key={`dot-${metric}-${payload.month}`}>
                  {/* Amber glow ring for low confidence points */}
                  {isLowConf && (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={8}
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      opacity={0.6}
                    />
                  )}
                  {/* Main dot */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={fill || metricColor}
                    stroke={stroke || metricColor}
                    strokeWidth={2}
                    className={isDrillable ? 'chart-dot-interactive' : ''}
                    style={{ opacity: isLowConf ? 0.7 : 1 }}
                  />
                </g>
              );
            };

            // Get gap keys for this metric
            const metricGapKeys = gapKeys[metric] || [];

            return (
              <React.Fragment key={metric}>
                {/* Dashed lines for each gap - one Line per gap segment */}
                {metricGapKeys.map((gapKey) => (
                  <Line
                    key={gapKey}
                    type="monotone"
                    dataKey={gapKey}
                    stroke={metricColor}
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                    activeDot={false}
                    connectNulls={true}
                    opacity={0.4}
                    legendType="none"
                  />
                ))}
                {/* Solid line for continuous data - rendered second (on top) */}
                <Line
                  key={`${metric}-solid`}
                  type="monotone"
                  dataKey={metric}
                  stroke={metricColor}
                  strokeWidth={2}
                  dot={renderCustomDot}
                  activeDot={isDrillable ? {
                    r: 6,
                    strokeWidth: 2,
                    cursor: 'pointer',
                    onClick: (dotData: any, event: any) => {

                      if (!onDrillDown || !data) return;

                      // Try to get the month from the event payload or use activeDataPoint
                      const monthFromPayload = event?.payload?.month || activeDataPoint?.label;

                      // Find all overlapping measures at this point
                      const overlappingMeasures = findOverlappingMeasures(metric, monthFromPayload);

                      if (overlappingMeasures.length > 1) {
                        // Multiple measures overlap - show disambiguation popup near the click
                        // Use the last tracked mouse position relative to the chart container
                        setDisambiguationPopup({
                          show: true,
                          x: lastClickPosition.current.x,
                          y: lastClickPosition.current.y,
                          measures: overlappingMeasures,
                          clickedData: dotData
                        });
                      } else {
                        // Single measure - proceed with normal drill-down
                        handleMeasureDrillDown(metric, dotData);
                      }
                    }
                  } : false}
                  connectNulls={false} // Don't connect lines where data is missing
                  className={isDrillable ? 'chart-line-interactive' : ''}
                  style={{ cursor: isDrillable ? 'pointer' : 'default' }}
                  legendType="none"
                />
              </React.Fragment>
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {selectedMetrics.length > 6 && (
        <div style={{ color: 'var(--warning-color)', fontSize: '12px', marginTop: '8px' }}>
          Maximum 6 metrics can be displayed simultaneously for optimal readability.
        </div>
      )}

      {/* Disambiguation Popup */}
      {disambiguationPopup.show && (
        <>
          {/* Backdrop to close popup */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              zIndex: 999
            }}
            onClick={() => setDisambiguationPopup(prev => ({ ...prev, show: false }))}
          />
          
          {/* Popup content - positioned near the clicked point */}
          <div
            style={{
              position: 'absolute',
              left: disambiguationPopup.x + 15,
              top: disambiguationPopup.y - 20,
              transform: (chartContainerRef.current && disambiguationPopup.x > chartContainerRef.current.clientWidth * 0.65)
                ? 'translateX(calc(-100% - 30px))' : 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #e1e5e9',
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(8px)',
              minWidth: '200px',
              maxWidth: '280px',
              zIndex: 1000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              marginBottom: '8px',
              textAlign: 'center'
            }}>
              Select measure to drill down:
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {disambiguationPopup.measures.map((measure, index) => (
                <div
                  key={measure.metric}
                  onClick={() => handleMeasureDrillDown(measure.metric)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: measure.color,
                        boxShadow: `0 0 0 2px ${measure.color}20`
                      }}
                    />
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {measure.metric === 'overallBrandEquityScore' ? 'Brand Equity' : measure.metric}
                    </span>
                  </div>
                  
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: measure.value >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {measure.value >= 0 ? '+' : ''}{measure.value.toFixed(1)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to get drill-down data
export const getDrillDownData = (
  parentMetric: string,
  monthlyData: IBrandAnalyticsMonthlyData[],
  level: 'measuredElement' | 'statements'
): string[] => {
  if (!monthlyData.length) {
    console.log('No monthly data available');
    return [];
  }

  if (level === 'measuredElement') {
    // Special case for overallBrandEquityScore - map to Brand Equity latent element
    if (parentMetric === 'overallBrandEquityScore') {
      parentMetric = 'Brand Equity';
    }

    // Find a month that has the latent element with inputs (not all months have data due to sample size)
    const monthWithData = monthlyData.find(month => {
      const latentElement = month.latentElementScores?.find(le => le.element === parentMetric);
      return latentElement?.inputs && latentElement.inputs.length > 0;
    });

    if (!monthWithData) {
      console.log('No month found with data for latent element:', parentMetric);
      return [];
    }

    // Find latent element and return ALL its input elements (both QC and LE)
    const latentElement = monthWithData.latentElementScores?.find(
      le => le.element === parentMetric
    );

    if (latentElement?.inputs) {
       // Return ALL inputs, not just QC
      return latentElement.inputs.map(input => input.name);
    } else {
      console.log('No inputs found for latent element');
      return [];
    }
  }
  
  if (level === 'statements') {
    // Find measured element and return its statements
    const measuredElement = monthlyData[0]?.questionCategoryScores?.find(
      me => me.category === parentMetric
    );
    return measuredElement?.input?.map(stmt => stmt.id) || [];
  }
  
  return [];
};

// Helper function to get available metrics from data
export const getAvailableMetrics = (data: IBrandAnalyticsMonthlyData[]): IBrandAnalyticsMetricOption[] => {
  if (!data.length) return [];

  const metrics: IBrandAnalyticsMetricOption[] = [];

  // Collect all unique latent elements from the data
  const latentElements = new Set<string>();
  data.forEach(month => {
    month.latentElementScores?.forEach(le => latentElements.add(le.element));
  });

  // Convert to array and create metric options
  latentElements.forEach(element => {
    metrics.push({
      key: element,
      label: element,
      type: 'latentElement',
      drillDownAvailable: true
    });
  });

  // Sort alphabetically with Brand Equity first (matching main chart dropdown)
  metrics.sort((a, b) => {
    // Always put Brand Equity first
    if (a.key === 'Brand Equity') return -1;
    if (b.key === 'Brand Equity') return 1;
    // Then sort alphabetically by label
    return a.label.localeCompare(b.label);
  });

  return metrics;
};

export default BrandAnalyticsChart;